import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Lock, Users, Activity, LayoutDashboard, Package, ChevronDown, Shield, Tag, FolderKanban, Inbox, Send } from 'lucide-react';
import DashboardHome from './DashboardHome';
import CredentialsList from './CredentialsList';
import CredentialDetail from './CredentialDetail';
import UsersManagement from './UsersManagement';
import AddCredential from './AddCredential';
import AuditLogs from './AuditLogs';
import CertificatesList from './CertificatesList';
import AddCertificate from './AddCertificate';
import CertificateDetail from './CertificateDetail';
import CategoriesManagement from './CategoriesManagement';
import ProjectsManagement from './ProjectsManagement';
import InboxPage from './InboxPage';
import SentPage from './SentPage';
import UserProfile from '@/components/UserProfile';
import ReleaseNotes from '@/components/ReleaseNotes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ setIsAuthenticated }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Best-effort cookie clear
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    navigate('/auth');
  }, [setIsAuthenticated, navigate]);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      toast.error('Session expired. Please login again.');
      handleLogout();
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-text-primary font-manrope text-xl">Loading...</div>
      </div>
    );
  }

  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path !== '/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <div className="bg-gradient-to-br from-primary to-primary-hover p-2 rounded-lg shadow-lg">
              <Package className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-manrope font-bold text-2xl text-text-primary tracking-tight">
              Keeboxs
            </h1>
          </div>
          <p className="text-sm text-text-muted mt-2">{user?.org_name}</p>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
              isActive('/dashboard') && location.pathname === '/dashboard'
                ? 'active'
                : ''
            }`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard size={20} strokeWidth={1.5} />
            Dashboard
          </button>

          <button
            onClick={() => navigate('/dashboard/credentials')}
            className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
              isActive('/dashboard/credentials') || isActive('/dashboard/add') || isActive('/dashboard/credential/')
                ? 'active'
                : ''
            }`}
            data-testid="nav-credentials"
          >
            <Lock size={20} strokeWidth={1.5} />
            Credentials
          </button>

          <button
            onClick={() => navigate('/dashboard/certificates')}
            className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
              isActive('/dashboard/certificates')
                ? 'active'
                : ''
            }`}
            data-testid="nav-certificates"
          >
            <Shield size={20} strokeWidth={1.5} />
            Certificates
          </button>

          <button
            onClick={() => navigate('/dashboard/inbox')}
            className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
              isActive('/dashboard/inbox') ? 'active' : ''
            }`}
            data-testid="nav-inbox"
          >
            <Inbox size={20} strokeWidth={1.5} />
            Inbox
          </button>

          <button
            onClick={() => navigate('/dashboard/sent')}
            className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
              isActive('/dashboard/sent') ? 'active' : ''
            }`}
            data-testid="nav-sent"
          >
            <Send size={20} strokeWidth={1.5} />
            Sent
          </button>

          <button
            onClick={() => navigate('/dashboard/projects')}
            className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
              isActive('/dashboard/projects') ? 'active' : ''
            }`}
            data-testid="nav-projects"
          >
            <FolderKanban size={20} strokeWidth={1.5} />
            Projects
          </button>

          {user?.role === 'admin' && (
            <>
              <button
                onClick={() => navigate('/dashboard/categories')}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
                  isActive('/dashboard/categories') ? 'active' : ''
                }`}
                data-testid="nav-categories"
              >
                <Tag size={20} strokeWidth={1.5} />
                Categories
              </button>

              <button
                onClick={() => navigate('/dashboard/users')}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
                  isActive('/dashboard/users')
                    ? 'active'
                    : ''
                }`}
                data-testid="nav-users"
              >
                <Users size={20} strokeWidth={1.5} />
                Team
              </button>

              <button
                onClick={() => navigate('/dashboard/audit-logs')}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all nav-item ${
                  isActive('/dashboard/audit-logs')
                    ? 'active'
                    : ''
                }`}
                data-testid="nav-audit-logs"
              >
                <Activity size={20} strokeWidth={1.5} />
                Audit Logs
              </button>
            </>
          )}
        </nav>

        {/* Fixed User Section at Bottom */}
        <div className="border-t border-gray-200 bg-white">
          {/* Version & Support Info */}
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <button
              onClick={() => setShowReleaseNotes(true)}
              className="w-full text-center hover:text-primary transition-colors group"
              data-testid="btn-release-notes"
            >
              <p className="text-[10px] text-text-muted leading-relaxed group-hover:text-primary transition-colors">
                v1.3.0 &middot; 09-Jul-2026 <span className="underline">Release Notes</span>
              </p>
            </button>
            <p className="text-[10px] text-text-muted text-center">
              &copy; 2026 Sysqua Solutions Pvt Ltd
            </p>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-full p-4 hover:bg-gray-50 transition-colors flex items-center gap-3"
              data-testid="btn-user-menu"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-hover flex items-center justify-center text-white font-semibold">
                {user?.full_name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-text-primary truncate">{user?.full_name}</p>
                <p className="text-xs text-text-muted truncate">{user?.email}</p>
              </div>
              <ChevronDown size={16} className={`text-text-muted transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 mx-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-3 border-b border-gray-100">
                  <span className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs font-medium uppercase rounded-full">
                    {user?.role}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setShowProfile(true);
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-sm font-medium text-text-primary"
                  data-testid="btn-view-profile"
                >
                  View Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 hover:bg-accent/10 transition-colors text-sm font-medium text-accent flex items-center gap-2"
                  data-testid="btn-logout"
                >
                  <LogOut size={16} strokeWidth={1.5} />
                  Logout
                </button>
                <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 space-y-0.5">
                  <p className="text-[10px] text-text-muted">Support: support@keeboxs.com</p>
                  <p className="text-[10px] text-text-muted">support@sysqua.com</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<DashboardHome user={user} />} />
          <Route path="/credentials" element={<CredentialsList user={user} />} />
          <Route path="/add" element={<AddCredential user={user} />} />
          <Route path="/credential/:id" element={<CredentialDetail user={user} />} />
          <Route path="/certificates" element={<CertificatesList user={user} />} />
          <Route path="/certificates/add" element={<AddCertificate user={user} />} />
          <Route path="/certificates/:id" element={<CertificateDetail user={user} />} />
          <Route path="/users" element={<UsersManagement user={user} />} />
          <Route path="/audit-logs" element={<AuditLogs user={user} />} />
          <Route path="/categories" element={<CategoriesManagement user={user} />} />
          <Route path="/projects" element={<ProjectsManagement user={user} />} />
          <Route path="/inbox" element={<InboxPage user={user} />} />
          <Route path="/sent" element={<SentPage user={user} />} />
        </Routes>
      </main>

      {showProfile && (
        <UserProfile onClose={() => setShowProfile(false)} user={user} />
      )}
      {showReleaseNotes && (
        <ReleaseNotes onClose={() => setShowReleaseNotes(false)} />
      )}
    </div>
  );
}
